/**
 * @module components/Auth/LoginForm
 * @description Formulario de inicio de sesión con panel de características del sistema.
 *
 * Pantalla de login con dos secciones:
 * - Izquierda: panel de marketing con las características principales del sistema ACSGC
 * - Derecha: formulario de email + password con validación y manejo de errores
 *
 * También incluye el flujo de "olvidé mi contraseña" que envía un email de recuperación
 * usando `AuthContext.resetPassword()`. El botón de demo llena automáticamente las
 * credenciales `admin@company.com` / `admin123` para demostración.
 *
 * @see AuthContext para las funciones login() y resetPassword()
 * @see ResetPasswordForm que se muestra cuando el usuario llega desde el email de recuperación
 */
import React, { useState } from 'react';
import { Mail, Lock, AlertCircle, Eye, EyeOff, ShieldCheck, BarChart3, FileCheck, Users, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Cumplimiento ISO 27001/27002',
    description: 'Gestión integral de controles y evidencias de seguridad.',
  },
  {
    icon: BarChart3,
    title: 'Dashboards en tiempo real',
    description: 'Métricas de cumplimiento y alertas críticas por nivel jerárquico.',
  },
  {
    icon: FileCheck,
    title: 'Auditoría completa',
    description: 'Trazabilidad total de cada cambio con registro automático.',
  },
  {
    icon: Users,
    title: 'Gestión de activos y riesgos',
    description: 'Inventario ISO 27005 con evaluación de amenazas y vulnerabilidades.',
  },
];

type View = 'login' | 'forgot' | 'forgot-sent';

export function LoginForm() {
  const { login, resetPassword, error, isLoading, clearError } = useAuth();
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await login(email, password);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setView('forgot-sent');
    } catch (err) {
      setResetError(
        err instanceof Error ? err.message : 'No se pudo enviar el correo. Intente nuevamente.'
      );
    } finally {
      setResetLoading(false);
    }
  };

  const goToLogin = () => {
    clearError();
    setResetEmail('');
    setView('login');
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel: Branding ── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-[#0B1E3D] flex-col justify-between p-12 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Glow accent */}
        <div className="absolute top-[-120px] left-[-120px] w-[500px] h-[500px] rounded-full bg-blue-600 opacity-10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-80px] right-[-80px] w-[350px] h-[350px] rounded-full bg-indigo-500 opacity-10 blur-[100px] pointer-events-none" />

        {/* Logo + product name */}
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-3">
            <img
              src="/acsgc-logo.png"
              alt="ACS GLOBAL CORE"
              className="h-14 w-auto object-contain"
            />
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-blue-400 uppercase">
                ACS GLOBAL CORE
              </p>
              <p className="text-[11px] text-slate-500 tracking-wide mt-0.5">
                Gestión de Cumplimiento
              </p>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-lg">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-5">
            Seguridad e información bajo control
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-12">
            Plataforma profesional de gestión de cumplimiento para marcos normativos ISO y regulaciones sectoriales en salud.
          </p>

          {/* Feature list */}
          <ul className="space-y-5">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <li key={title} className="flex items-start space-x-4">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-blue-400" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom legal */}
        <div className="relative z-10">
          <p className="text-[11px] text-slate-600">
            © {new Date().getFullYear()} ACS Global Core. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* ── Right panel: Form ── */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col justify-center bg-white dark:bg-gray-950 px-8 sm:px-16 lg:px-20 py-12">

        {/* Mobile logo */}
        <div className="flex justify-center mb-10 lg:hidden">
          <img src="/acsgc-logo.png" alt="ACS GLOBAL CORE" className="h-16 w-auto object-contain" />
        </div>

        {/* ── VIEW: Login ── */}
        {view === 'login' && (
          <>
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                Bienvenido
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Ingrese sus credenciales para acceder al sistema.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3.5 flex items-start space-x-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300 leading-snug">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all outline-none"
                    placeholder="usuario@organizacion.cl"
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => { clearError(); setView('forgot'); }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-11 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all outline-none"
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-2 py-3.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center space-x-2 shadow-sm"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Verificando acceso…</span>
                  </>
                ) : (
                  <span>Iniciar sesión</span>
                )}
              </button>
            </form>
          </>
        )}

        {/* ── VIEW: Forgot password ── */}
        {view === 'forgot' && (
          <>
            <button
              type="button"
              onClick={goToLogin}
              className="flex items-center space-x-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-10 transition-colors w-fit"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al inicio de sesión</span>
            </button>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                Recuperar contraseña
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                Ingrese el correo electrónico de su cuenta y le enviaremos un enlace para restablecer su contraseña.
              </p>
            </div>

            {resetError && (
              <div className="mb-6 p-3.5 flex items-start space-x-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300 leading-snug">{resetError}</p>
              </div>
            )}

            <form onSubmit={handleForgot} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all outline-none"
                    placeholder="usuario@organizacion.cl"
                    required
                    disabled={resetLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full py-3.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center space-x-2 shadow-sm"
              >
                {resetLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Enviando…</span>
                  </>
                ) : (
                  <span>Enviar enlace de recuperación</span>
                )}
              </button>
            </form>
          </>
        )}

        {/* ── VIEW: Forgot sent ── */}
        {view === 'forgot-sent' && (
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-3">
              Correo enviado
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs mb-2">
              Si la dirección <span className="font-medium text-gray-700 dark:text-gray-300">{resetEmail}</span> está registrada, recibirá un enlace para restablecer su contraseña.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-600 mb-10">
              Revise también su carpeta de spam.
            </p>
            <button
              type="button"
              onClick={goToLogin}
              className="flex items-center space-x-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al inicio de sesión</span>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center space-y-1">
          <p className="text-[11px] text-gray-400 dark:text-gray-600">
            ACS Global Core &nbsp;·&nbsp; v2.0.0
          </p>
          <p className="text-[11px] text-gray-300 dark:text-gray-700">
            Plataforma certificada ISO 27001 / 27002 / 27005
          </p>
        </div>
      </div>
    </div>
  );
}
